// frontend/packages/business-console/src/components/inbox/AddToCrmButton.jsx

import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";

/**
 * "Add to CRM" promotion control. Calls
 * POST /api/v2/inbox/conversations/:id/promote-to-crm which sets
 * Customer.inCrm=true and writes an inbox_promoted activity to the CRM
 * timeline. Once promoted, the button flips to "View in CRM" and routes
 * the user to the customer detail page.
 *
 * Props:
 *   onPromote        — async () => Promise; throws on failure.
 *   alreadyInCrm     — initial state.
 *   crmHref          — destination once promoted (e.g. /business/<slug>/crm/<customerId>).
 *                      Required for the post-promote state to be useful.
 *   disabled         — disables the trigger.
 */
export default function AddToCrmButton({
  onPromote,
  alreadyInCrm,
  crmHref,
  disabled,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [promoted, setPromoted] = useState(Boolean(alreadyInCrm));

  React.useEffect(() => {
    setPromoted(Boolean(alreadyInCrm));
  }, [alreadyInCrm]);

  const handleClick = async () => {
    setBusy(true);
    setError("");
    try {
      await onPromote();
      setPromoted(true);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Could not add to CRM");
    } finally {
      setBusy(false);
    }
  };

  if (promoted) {
    return crmHref ? (
      <Button
        variant="outlined"
        component={RouterLink}
        to={crmHref}
        startIcon={<OpenInNewOutlinedIcon />}
        sx={{ textTransform: "none" }}
      >
        View in CRM
      </Button>
    ) : (
      <Button variant="outlined" disabled sx={{ textTransform: "none" }}>
        In CRM
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={busy || disabled}
        startIcon={
          busy ? <CircularProgress size={16} /> : <PersonAddOutlinedIcon />
        }
        sx={{ textTransform: "none" }}
      >
        {busy ? "Adding…" : "Add to CRM"}
      </Button>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Added to CRM
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
